import {
  CompanyOfferCard,
  OfferContextSnapshot,
  OfferPlanCode,
  OfferUpsellContent,
  UpsellContext,
} from "../../shared/types/offer-upsell";
import { SubscriptionPlanMetadata } from "../models/subscription-plan.metadata";
import { Organization } from "../models/organizations.entity";

const OFFER_CODES: OfferPlanCode[] = [ "wetalk", "wetalk-plus", "wetalk-coach" ];

function isOfferPlanCode(value?: string | null): value is OfferPlanCode {
  return !!value && OFFER_CODES.includes(value as OfferPlanCode);
}

function getPlanName(planCode: OfferPlanCode): string {
  return SubscriptionPlanMetadata[planCode]?.name ?? planCode;
}

function getRecommendedPlan(context: UpsellContext, currentPlanCode: OfferPlanCode | "legacy"): OfferPlanCode {
  if (context === "resources") {
    return currentPlanCode === "wetalk-plus" ? "wetalk-coach" : "wetalk-plus";
  }

  if (context === "journeys") {
    return "wetalk-coach";
  }

  if (context === "orientation") {
    return "wetalk";
  }

  return currentPlanCode === "wetalk-plus" ? "wetalk-coach" : "wetalk-plus";
}

export function getOfferContextSnapshot(organization?: Organization | null): OfferContextSnapshot {
  const managedPlanCode = organization?.activeSubscription?.planCode;
  if (isOfferPlanCode(managedPlanCode)) {
    return {
      currentPlanCode: managedPlanCode,
      currentPlanLabel: getPlanName(managedPlanCode),
    };
  }

  const subscriptionPlanCode = organization?.subscriptionPlan?.slug;
  if (isOfferPlanCode(subscriptionPlanCode)) {
    return {
      currentPlanCode: subscriptionPlanCode,
      currentPlanLabel: getPlanName(subscriptionPlanCode),
    };
  }

  const legacyLabel = organization?.subscriptionPlan?.slug
    ? SubscriptionPlanMetadata[organization.subscriptionPlan.slug]?.name ?? organization.subscriptionPlan.slug
    : undefined;

  return {
    currentPlanCode: "legacy",
    currentPlanLabel: legacyLabel,
  };
}

export function getOfferUpsellContent(
  context: UpsellContext,
  organization?: Organization | null,
): OfferUpsellContent {
  const snapshot = getOfferContextSnapshot(organization);
  const recommendedPlan = getRecommendedPlan(context, snapshot.currentPlanCode);
  const recommendedPlanName = getPlanName(recommendedPlan);

  if (context === "resources") {
    return {
      eyebrow: "Offres entreprise 2026",
      title: "Débloquez toute la bibliothèque We talk",
      body: "Les nouvelles offres entreprise rendent la plateforme plus utile au quotidien en ouvrant l'accès complet aux contenus et aux outils de pilotage.",
      benefits: [
        "Accès à l'ensemble des ressources, et plus seulement à celles liées aux parcours en cours",
        "Formations, statistiques et tableau de paramétrage inclus dès We talk Plus",
        "Appel d'orientation offert sur les trois offres entreprise",
      ],
      primaryCtaLabel: `Découvrir ${recommendedPlanName}`,
      secondaryCtaLabel: "Être recontacté",
      recommendedPlan,
      currentPlanLabel: snapshot.currentPlanLabel,
    };
  }

  if (context === "orientation") {
    return {
      eyebrow: "Offres entreprise 2026",
      title: "Activez l'appel d'orientation pour vos collaborateurs",
      body: "Les nouvelles offres entreprise incluent l'appel d'orientation sans consommation de crédit, pour orienter plus vite les usages.",
      benefits: [
        "0 crédit consommé pour l'appel d'orientation sur les trois offres entreprise",
        "1 parcours de Parole = 1 parcours de Coaching = 10 crédits",
        "Une expérience plus simple à expliquer et à activer côté entreprise",
      ],
      primaryCtaLabel: `Découvrir ${recommendedPlanName}`,
      secondaryCtaLabel: "Être recontacté",
      recommendedPlan,
      currentPlanLabel: snapshot.currentPlanLabel,
    };
  }

  if (context === "journeys") {
    return {
      eyebrow: "Offres entreprise 2026",
      title: "Débloquez plus facilement les parcours",
      body: "Les nouvelles offres entreprise rendent la logique crédits plus lisible et facilitent l'accès aux parcours au bon moment.",
      benefits: [
        "We talk Basic et We talk Plus fonctionnent sans stock de crédits inclus pour les parcours",
        "1 parcours de Parole = 1 parcours de Coaching = 10 crédits",
        "We talk coach inclut 10 crédits par collaborateur, soit 1 parcours par personne",
      ],
      primaryCtaLabel: `Découvrir ${recommendedPlanName}`,
      secondaryCtaLabel: "Être recontacté",
      recommendedPlan,
      currentPlanLabel: snapshot.currentPlanLabel,
    };
  }

  return {
    eyebrow: "Offres entreprise 2026",
    title: "Choisissez l'offre qui active vraiment la plateforme",
    body: "Les nouvelles offres entreprise rendent les droits d'accès, les crédits et l'accompagnement plus lisibles pour les collaborateurs comme pour les équipes pilotage.",
    benefits: [
      "Appel d'orientation offert sur les trois offres entreprise",
      "1 parcours de Parole = 1 parcours de Coaching = 10 crédits",
      "Montée en gamme claire entre We talk Basic, We talk Plus et We talk coach",
    ],
    primaryCtaLabel: `Découvrir ${recommendedPlanName}`,
    secondaryCtaLabel: "Être recontacté",
    recommendedPlan,
    currentPlanLabel: snapshot.currentPlanLabel,
  };
}

export function getCompanyOfferCard(planCode?: OfferPlanCode | null): CompanyOfferCard | undefined {
  if (!planCode) {
    return undefined;
  }

  return getCompanyOfferCards().find((card) => card.planCode === planCode);
}

export function getRecommendedOfferPlan(currentPlanCode: OfferPlanCode | "legacy"): OfferPlanCode | undefined {
  if (currentPlanCode === "wetalk-plus") {
    return "wetalk-coach";
  }

  if (currentPlanCode === "wetalk-coach") {
    return undefined;
  }

  return "wetalk-plus";
}

export function getCompanyOfferCards(): CompanyOfferCard[] {
  return [
    {
      planCode: "wetalk",
      kicker: "L'essentiel pour lancer l'usage",
      summary: "Parcours, appel d'orientation offert, collaborateurs et ressources liées aux parcours en cours.",
      creditSummary: "Aucun crédit inclus, parcours à 10 crédits selon besoin",
      highlights: [
        "BE",
        "Parcours",
        "Appel d'orientation",
        "Collaborateurs",
        "Ressources liées aux parcours",
      ],
    },
    {
      planCode: "wetalk-plus",
      kicker: "L'offre pour activer l'usage complet",
      summary: "Débloque l'ensemble des contenus et les outils de pilotage sans changer l'expérience collaborateur.",
      creditSummary: "Aucun crédit inclus, parcours à 10 crédits selon besoin",
      highlights: [
        "BE",
        "Ressources complètes",
        "Formations",
        "Statistiques",
        "Tableau de paramétrage",
      ],
    },
    {
      planCode: "wetalk-coach",
      kicker: "L'offre pour industrialiser l'accompagnement",
      summary: "Ajoute 10 crédits inclus par collaborateur, soit 1 parcours par personne, pour faciliter le passage à l'action.",
      creditSummary: "10 crédits inclus / collaborateur + quota 1 parcours / personne",
      highlights: [
        "BE",
        "Ressources complètes",
        "Formations",
        "Statistiques",
        "10 crédits / collaborateur",
      ],
    },
  ];
}
